// src/services/brandService.ts
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  Timestamp,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';

const MAX_FEATURED_BRANDS = 6;

export interface Brand {
  id?: string;
  name: string;
  description: string;
  imageUrl: string;
  slug: string;
  isActive: boolean;
  featured: boolean;
  websiteUrl?: string;
  createdBy: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

export interface PaginatedBrandsResult {
  brands: Brand[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface BrandStats {
  total: number;
  active: number;
  inactive: number;
  featured: number;
}

// ── Slug generator ────────────────────────────────────────────────────────────
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// ── Storage helpers ───────────────────────────────────────────────────────────
const uploadBrandImage = async (file: File, brandName: string): Promise<string> => {
  const timestamp = Date.now();
  const fileName = `${generateSlug(brandName)}_${timestamp}`;
  const imageRef = ref(storage, `brands/${fileName}`);
  const snapshot = await uploadBytes(imageRef, file);
  return getDownloadURL(snapshot.ref);
};

/**
 * Delete a brand image from Firebase Storage by its download URL.
 * Resolves silently if the file no longer exists.
 */
const deleteBrandImage = async (imageUrl: string): Promise<void> => {
  if (!imageUrl) return;
  try {
    // Firebase Storage download URLs contain the encoded path after "/o/"
    const urlParts = imageUrl.split('/o/');
    if (urlParts.length >= 2) {
      const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
      const imageRef = ref(storage, filePath);
      await deleteObject(imageRef);
    } else {
      // Fallback: treat the raw value as a storage path
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    }
  } catch (error: any) {
    // Ignore "object not found" — image may already be gone
    if (error?.code !== 'storage/object-not-found') {
      console.error('Error deleting brand image:', error);
    }
  }
};

/* ===================== GET BRANDS PAGINATED =====================
 *
 * Uses a two-step cursor-seek so any page can be fetched in any order
 * (page 1 → page 5 → page 2 all work correctly).
 *
 *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
 *             seek). The last doc from this set becomes our startAfter cursor.
 *   Step 2 — fetch the real pageSize docs starting after that cursor.
 *
 * For page 1 the seek is skipped entirely (skip count = 0).
 * ================================================================ */
export const fetchBrandsPaginated = async (
  pageSize: number = 10,
  page: number = 1,
  searchQuery?: string,
  statusFilter?: string
): Promise<PaginatedBrandsResult> => {
  try {
    const brands: Brand[] = [];
    const collectionRef = collection(db, 'brands');

    // Build where-constraints (shared between count + seek + page queries)
    const whereConstraints: any[] = [];

    if (statusFilter === 'active') {
      whereConstraints.push(where('isActive', '==', true));
    } else if (statusFilter === 'inactive') {
      whereConstraints.push(where('isActive', '==', false));
    } else if (statusFilter === 'featured') {
      whereConstraints.push(where('featured', '==', true));
    }

    const orderConstraint = orderBy('createdAt', 'desc');

    // ── Total count (0 doc reads) ──────────────────────────────────────────
    const countSnapshot = await getCountFromServer(
      query(collectionRef, ...whereConstraints)
    );
    const totalCount = countSnapshot.data().count;

    // ── Step 1: cursor seek ────────────────────────────────────────────────
    let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    const skipCount = (page - 1) * pageSize;

    if (skipCount > 0) {
      const seekSnapshot = await getDocs(
        query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
      );
      if (!seekSnapshot.empty) {
        cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
      }
    }

    // ── Step 2: fetch the real page ────────────────────────────────────────
    const pageQuery: Query<DocumentData> = cursorDoc
      ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
      : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

    const snapshot = await getDocs(pageQuery);

    // ── Map + client-side search filter ───────────────────────────────────
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const brand: Brand = {
        id: docSnap.id,
        name: data.name || '',
        description: data.description || '',
        imageUrl: data.imageUrl || '',
        slug: data.slug || '',
        isActive: data.isActive !== false,
        featured: data.featured || false,
        websiteUrl: data.websiteUrl,
        createdBy: data.createdBy || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };

      if (searchQuery && searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          brand.name.toLowerCase().includes(searchLower) ||
          brand.description.toLowerCase().includes(searchLower) ||
          brand.slug.toLowerCase().includes(searchLower) ||
          (brand.websiteUrl && brand.websiteUrl.toLowerCase().includes(searchLower));

        if (matchesSearch) brands.push(brand);
      } else {
        brands.push(brand);
      }
    });

    return {
      brands,
      totalCount,
      lastVisible: null,  // no longer used by callers
      firstVisible: null, // no longer used by callers
    };
  } catch (error) {
    console.error('Error fetching paginated brands:', error);
    throw error;
  }
};

/* ===================== GET BRAND STATS ===================== */
export const fetchBrandStats = async (): Promise<BrandStats> => {
  try {
    const col = collection(db, 'brands');

    const [totalSnap, activeSnap, inactiveSnap, featuredSnap] = await Promise.all([
      getCountFromServer(query(col)),
      getCountFromServer(query(col, where('isActive', '==', true))),
      getCountFromServer(query(col, where('isActive', '==', false))),
      getCountFromServer(query(col, where('featured', '==', true))),
    ]);

    return {
      total: totalSnap.data().count,
      active: activeSnap.data().count,
      inactive: inactiveSnap.data().count,
      featured: featuredSnap.data().count,
    };
  } catch (error) {
    console.error('Error fetching brand stats:', error);
    throw error;
  }
};

/* ===================== CREATE BRAND ===================== */
export const createBrand = async (
  brandData: Omit<Brand, 'id' | 'createdAt' | 'updatedAt' | 'imageUrl' | 'slug'>,
  imageFile: File,
  userId: string
): Promise<{ success: boolean; message: string; brandId?: string }> => {
  try {
    const imageUrl = await uploadBrandImage(imageFile, brandData.name);
    const slug = generateSlug(brandData.name);

    const brandRef = await addDoc(collection(db, 'brands'), {
      ...brandData,
      imageUrl,
      slug,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: 'Brand created successfully!',
      brandId: brandRef.id,
    };
  } catch (error) {
    console.error('Error creating brand:', error);
    return { success: false, message: 'Failed to create brand. Please try again.' };
  }
};

/* ===================== UPDATE BRAND ===================== */
export const updateBrand = async (
  brandId: string,
  brandData: Partial<Omit<Brand, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>,
  imageFile?: File
): Promise<{ success: boolean; message: string }> => {
  try {
    const brandRef = doc(db, 'brands', brandId);
    const brandSnap = await getDoc(brandRef);

    if (!brandSnap.exists()) {
      return { success: false, message: 'Brand not found.' };
    }

    const updateData: any = {
      ...brandData,
      updatedAt: serverTimestamp(),
    };

    if (brandData.name) {
      updateData.slug = generateSlug(brandData.name);
    }

    if (imageFile) {
      // Upload new image, then delete the old one
      const oldImageUrl = brandSnap.data().imageUrl;
      const newImageUrl = await uploadBrandImage(imageFile, brandData.name || brandSnap.data().name);
      updateData.imageUrl = newImageUrl;
      if (oldImageUrl) {
        await deleteBrandImage(oldImageUrl);
      }
    }

    await updateDoc(brandRef, updateData);

    return { success: true, message: 'Brand updated successfully!' };
  } catch (error) {
    console.error('Error updating brand:', error);
    return { success: false, message: 'Failed to update brand. Please try again.' };
  }
};

/* ===================== REMOVE BRAND IMAGE =====================
 *
 * Deletes the brand image from Firebase Storage and clears the
 * imageUrl field in Firestore. Called when the admin explicitly
 * removes an image from an existing brand in the edit drawer.
 * ============================================================== */
export const removeBrandImage = async (
  brandId: string,
  imageUrl: string
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!imageUrl) {
      return { success: true, message: 'No image to remove.' };
    }

    // Delete from Storage first
    await deleteBrandImage(imageUrl);

    // Clear the imageUrl field in Firestore
    const brandRef = doc(db, 'brands', brandId);
    await updateDoc(brandRef, {
      imageUrl: '',
      updatedAt: serverTimestamp(),
    });

    return { success: true, message: 'Image removed successfully.' };
  } catch (error) {
    console.error('Error removing brand image:', error);
    return { success: false, message: 'Failed to remove image. Please try again.' };
  }
};

/* ===================== DELETE BRAND ===================== */
export const deleteBrand = async (
  brandId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const brandRef = doc(db, 'brands', brandId);
    const brandSnap = await getDoc(brandRef);

    if (!brandSnap.exists()) {
      return { success: false, message: 'Brand not found.' };
    }

    const imageUrl = brandSnap.data().imageUrl;
    if (imageUrl) {
      await deleteBrandImage(imageUrl);
    }

    await deleteDoc(brandRef);

    return { success: true, message: 'Brand deleted successfully!' };
  } catch (error) {
    console.error('Error deleting brand:', error);
    return { success: false, message: 'Failed to delete brand. Please try again.' };
  }
};

/**
 * @deprecated Use fetchBrandsPaginated instead for better performance.
 * Kept for backwards compatibility with any other module that may still call it.
 */
export const fetchBrands = async (): Promise<Brand[]> => {
  try {
    const brandsRef = collection(db, 'brands');
    const q = query(brandsRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        description: data.description,
        imageUrl: data.imageUrl,
        slug: data.slug,
        isActive: data.isActive,
        featured: data.featured,
        websiteUrl: data.websiteUrl,
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Brand;
    });
  } catch (error) {
    console.error('Error fetching brands:', error);
    return [];
  }
};

/* ===================== FETCH ACTIVE BRANDS ===================== */
export const fetchActiveBrands = async (): Promise<Brand[]> => {
  try {
    const brandsRef = collection(db, 'brands');
    const q = query(brandsRef, where('isActive', '==', true), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Brand;
    });
  } catch (error) {
    console.error('Error fetching active brands:', error);
    return [];
  }
};

/* ===================== FETCH FEATURED BRANDS ===================== */
export const fetchFeaturedBrands = async (): Promise<Brand[]> => {
  try {
    const brandsRef = collection(db, 'brands');
    const q = query(
      brandsRef,
      where('featured', '==', true),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Brand;
    });
  } catch (error) {
    console.error('Error fetching featured brands:', error);
    return [];
  }
};

/* ===================== FETCH BRAND BY ID ===================== */
export const fetchBrandById = async (brandId: string): Promise<Brand | null> => {
  try {
    const brandRef = doc(db, 'brands', brandId);
    const brandSnap = await getDoc(brandRef);
    if (brandSnap.exists()) {
      const data = brandSnap.data();
      return {
        id: brandSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Brand;
    }
    return null;
  } catch (error) {
    console.error('Error fetching brand:', error);
    return null;
  }
};

/* ===================== FETCH BRAND BY SLUG ===================== */
export const fetchBrandBySlug = async (slug: string): Promise<Brand | null> => {
  try {
    const brandsRef = collection(db, 'brands');
    const q = query(brandsRef, where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Brand;
    }
    return null;
  } catch (error) {
    console.error('Error fetching brand by slug:', error);
    return null;
  }
};

// Export the constant so the component can use it
export { MAX_FEATURED_BRANDS };